import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Fish, Plus, Pencil, Trash2, Sparkles, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const fishSpeciesSchema = z.object({
  common_name: z.string().min(1, "Common name is required").max(100),
  scientific_name: z.string().min(1, "Scientific name is required").max(100),
  category: z.string().min(1, "Category is required"),
  subcategory: z.string().optional(),
  typical_size_min: z.number().min(0).optional(),
  typical_size_max: z.number().min(0).optional(),
  habitat_depth: z.string().optional(),
  region: z.string().optional(),
  rarity: z.string().optional(),
  difficulty_tier: z.number().int().min(1).max(5).default(3),
  notes: z.string().optional(),
});

type FishSpeciesFormData = z.infer<typeof fishSpeciesSchema>;

interface FishSpecies extends FishSpeciesFormData {
  id: number;
  created_at: string;
  updated_at: string;
}

export const AdminFishSpecies = () => {
  const [species, setSpecies] = useState<FishSpecies[]>([]);
  const [loading, setLoading] = useState(true);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSpecies, setEditingSpecies] = useState<FishSpecies | null>(null);
  const { toast } = useToast();

  const form = useForm<FishSpeciesFormData>({
    resolver: zodResolver(fishSpeciesSchema),
    defaultValues: {
      common_name: "",
      scientific_name: "",
      category: "",
      subcategory: "",
      typical_size_min: undefined,
      typical_size_max: undefined,
      habitat_depth: "",
      region: "",
      rarity: "",
      difficulty_tier: 3,
      notes: "",
    },
  });

  const fetchSpecies = async () => {
    try {
      const { data, error } = await supabase
        .from("fish_species")
        .select("*")
        .order("common_name");

      if (error) throw error;
      setSpecies(data || []);
    } catch (error) {
      console.error("Error fetching fish species:", error);
      toast({
        title: "Error",
        description: "Failed to load fish species",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecies();
  }, []);

  const fetchFromWikipedia = async () => {
    const commonName = form.getValues("common_name");
    if (!commonName) {
      toast({
        title: "Error",
        description: "Please enter a common name first",
        variant: "destructive",
      });
      return;
    }

    setWikiLoading(true);
    try {
      // Search Wikipedia for the fish
      const searchResponse = await fetch(
        `https://en.wikipedia.org/w/api.php?action=opensearch&format=json&search=${encodeURIComponent(commonName)}&origin=*`
      );
      const searchData = await searchResponse.json();
      
      if (!searchData[1] || searchData[1].length === 0) {
        toast({
          title: "Not Found",
          description: "No Wikipedia article found for this fish",
        });
        return;
      }

      const pageTitle = searchData[1][0];
      
      // Get page content
      const contentResponse = await fetch(
        `https://en.wikipedia.org/w/api.php?action=query&format=json&prop=extracts|revisions&titles=${encodeURIComponent(pageTitle)}&exintro=1&explaintext=1&rvprop=content&origin=*`
      );
      const contentData = await contentResponse.json();
      const page = Object.values(contentData.query.pages)[0] as any;
      
      if (!page || !page.extract) {
        toast({
          title: "Error",
          description: "Could not fetch Wikipedia content",
          variant: "destructive",
        });
        return;
      }

      const extract = page.extract;
      const infobox = page.revisions?.[0]?.['*'] || '';

      // Extract scientific name from infobox
      const scientificNameMatch = infobox.match(/\|\s*binomial\s*=\s*([^\n|]+)/i) ||
                                   infobox.match(/\|\s*name\s*=\s*''+([^']+)''+/i);
      
      // Try to extract size information
      const sizeMatch = extract.match(/(\d+)[\s-]+(?:to|and)[\s-]+(\d+)\s*(?:lb|pound|kg|kilogram)/i);
      
      // Extract habitat info
      const habitatMatch = extract.match(/(?:found|inhabit|live|occur)(?:s|ing)?\s+(?:in|at|on)\s+([^.]{10,100})/i);

      // Auto-populate form
      if (scientificNameMatch && !form.getValues("scientific_name")) {
        form.setValue("scientific_name", scientificNameMatch[1].trim());
      }

      if (sizeMatch && !form.getValues("typical_size_min")) {
        form.setValue("typical_size_min", parseInt(sizeMatch[1]));
        form.setValue("typical_size_max", parseInt(sizeMatch[2]));
      }

      if (habitatMatch && !form.getValues("habitat_depth")) {
        form.setValue("habitat_depth", habitatMatch[1].trim());
      }

      // Set notes to first paragraph
      const firstParagraph = extract.split('\n')[0];
      if (firstParagraph && !form.getValues("notes")) {
        form.setValue("notes", firstParagraph.substring(0, 500));
      }

      toast({
        title: "Success",
        description: "Information fetched from Wikipedia!",
      });
    } catch (error) {
      console.error("Wikipedia fetch error:", error);
      toast({
        title: "Error",
        description: "Failed to fetch data from Wikipedia",
        variant: "destructive",
      });
    } finally {
      setWikiLoading(false);
    }
  };

  const handleSave = async (values: FishSpeciesFormData) => {
    try {
      if (editingSpecies) {
        const { error } = await supabase
          .from("fish_species")
          .update(values)
          .eq("id", editingSpecies.id);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Fish species updated successfully",
        });
      } else {
        const { error } = await supabase
          .from("fish_species")
          .insert([values as any]);

        if (error) throw error;

        toast({
          title: "Success",
          description: "Fish species created successfully",
        });
      }

      setDialogOpen(false);
      setEditingSpecies(null);
      form.reset();
      fetchSpecies();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to save fish species",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (fish: FishSpecies) => {
    setEditingSpecies(fish);
    form.reset({
      common_name: fish.common_name,
      scientific_name: fish.scientific_name,
      category: fish.category,
      subcategory: fish.subcategory || "",
      typical_size_min: fish.typical_size_min || undefined,
      typical_size_max: fish.typical_size_max || undefined,
      habitat_depth: fish.habitat_depth || "",
      region: fish.region || "",
      rarity: fish.rarity || "",
      difficulty_tier: fish.difficulty_tier,
      notes: fish.notes || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Are you sure you want to delete this fish species?")) return;

    try {
      const { error } = await supabase
        .from("fish_species")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Fish species deleted successfully",
      });

      fetchSpecies();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete fish species",
        variant: "destructive",
      });
    }
  };

  const handleDialogClose = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingSpecies(null);
      form.reset();
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity?.toLowerCase()) {
      case "rare": return "bg-destructive/10 text-destructive";
      case "uncommon": return "bg-primary/10 text-primary";
      default: return "bg-muted text-muted-foreground";
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading fish species...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Fish className="h-5 w-5" />
          <h2 className="text-2xl font-bold">Fish Species ({species.length})</h2>
        </div>
        <Dialog open={dialogOpen} onOpenChange={handleDialogClose}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Species
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSpecies ? "Edit" : "Create"} Fish Species</DialogTitle>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                <div className="flex gap-2">
                  <FormField
                    control={form.control}
                    name="common_name"
                    render={({ field }) => (
                      <FormItem className="flex-1">
                        <FormLabel>Common Name *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Yellowfin Tuna" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex items-end">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={fetchFromWikipedia}
                      disabled={wikiLoading}
                      className="gap-2"
                    >
                      {wikiLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Sparkles className="h-4 w-4" />
                      )}
                      Wiki Lookup
                    </Button>
                  </div>
                </div>

                <FormField
                  control={form.control}
                  name="scientific_name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Scientific Name *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Thunnus albacares" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Pelagic">Pelagic</SelectItem>
                            <SelectItem value="Demersal">Demersal</SelectItem>
                            <SelectItem value="Inshore">Inshore</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="subcategory"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subcategory</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Tuna, Billfish, etc." />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="typical_size_min"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Min Size (lbs)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="typical_size_max"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Max Size (lbs)</FormLabel>
                        <FormControl>
                          <Input 
                            type="number" 
                            {...field}
                            value={field.value || ""}
                            onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : undefined)}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="habitat_depth"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Habitat / Depth</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Open ocean, surface to 300 ft" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="region"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Region</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Tropical Pacific" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="rarity"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Rarity</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select rarity" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="Abundant">Abundant</SelectItem>
                            <SelectItem value="Common">Common</SelectItem>
                            <SelectItem value="Uncommon">Uncommon</SelectItem>
                            <SelectItem value="Rare">Rare</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="difficulty_tier"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Difficulty Tier (1-5)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          min={1}
                          max={5}
                          {...field}
                          onChange={(e) => field.onChange(parseInt(e.target.value) || 3)}
                        />
                      </FormControl>
                      <FormDescription>
                        1 = Easy, 5 = Expert
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea 
                          {...field} 
                          placeholder="Additional information about the species"
                          rows={4}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button type="submit" className="w-full">
                  {editingSpecies ? "Update" : "Create"} Fish Species
                </Button>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      {species.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No fish species found
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {species.map((fish) => (
            <Card key={fish.id}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{fish.common_name}</h3>
                      {fish.rarity && (
                        <Badge className={getRarityColor(fish.rarity)}>
                          {fish.rarity}
                        </Badge>
                      )}
                      <Badge variant="outline">Tier {fish.difficulty_tier}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground italic mb-2">
                      {fish.scientific_name}
                    </p>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <div><span className="font-medium">Category:</span> {fish.category} {fish.subcategory && `(${fish.subcategory})`}</div>
                      {(fish.typical_size_min || fish.typical_size_max) && (
                        <div><span className="font-medium">Size:</span> {fish.typical_size_min}-{fish.typical_size_max} lbs</div>
                      )}
                      {fish.region && <div><span className="font-medium">Region:</span> {fish.region}</div>}
                      {fish.habitat_depth && <div><span className="font-medium">Habitat:</span> {fish.habitat_depth}</div>}
                    </div>
                    {fish.notes && (
                      <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                        {fish.notes}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(fish)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(fish.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
